import { ConfigService } from '@axo/config/ConfigService';
import { Consent } from '@axo/form/feature/axo-form-v2/lib/components';
import styles from '@axo/form/feature/axo-form-v2/lib/forms/Sweden/LoanApplication/loan.module.scss';
import { EmailFieldUI } from '@axo/form/feature/form-input/EmailField/EmailFieldUI';
import { LoanDurationField } from '@axo/form/feature/form-input/LoanDurationField';
import { NumberFieldUI } from '@axo/form/feature/form-input/NumberField/NumberFieldUI';
import { PhoneFieldUI } from '@axo/form/feature/form-input/PhoneField/PhoneFieldUI';
import { RadioFieldUI } from '@axo/form/feature/form-input/RadioField/RadioFieldUI';
import SliderFieldUI from '@axo/form/feature/form-input/SliderField/SliderFieldUI';
import { SliderField } from '@axo/product/common/components/form/core/SliderField';
import { Purpose } from '@axo/shared/data-access/types';
import { useTranslation } from '@axo/shared/i18n';
import { useExperimentFlag } from '@axo/shared/services/feature-flags/useExperimentFlag';
import { Button } from '@axo/ui-core/components/Button';
import { Icon } from '@axo/ui-core/components/Icon';
import { Stack } from '@axo/ui-core/components/layout/item';
import { Message } from '@axo/ui-core/components/Message';
import { USPsList } from '@axo/ui-feature/components/USPsList';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Trans } from 'react-i18next';
import { Form } from '../../../../../components/Form';
import { Navigation } from '../../../../../components/Navigation';
import { useAxoFormContext } from '../../../../../context/AxoForm/AxoForm';
import useInitiatedTracking from '../../../../../hooks/tracking/useInitiatedTracking';
import { useAxoFormDefaultValues } from '../../../../../hooks/useAxoFormDefaultValues';
import { useMarketingConsent } from '../../../../../hooks/useMarketingConsent';
import type { FormViewOptions } from '../../../../../types';
import { formatSpacedNumber } from '../../../../../utils/format';
import { calculateMonthlyPayment } from '../../../../../utils/monthlyPayment';
import { useEmailValidation } from '../../../../../utils/useEmailValidation';
import { verifyThreshold } from '../../../../../utils/verifyThreshold';
import { Step1Data } from '../../../FormData.fi.types';
import { initialPurposeOptions } from '../../config/globals';
import { finlandStep1Schema } from '../../config/schemas/Finland.Step1.schema';
import { RefinanceSaving } from '../../RefinanceSaving';
import { getEmailRegisterOptions, phoneFieldOptions } from '../shared-config';
import { initialPurposeFieldOptions } from './config';

const touchedFieldsOverride = [
  'LoanApplication/AppliedAmount',
  'LoanApplication/LoanDuration',
];

export const Step1 = ({ isPre, options }: FormViewOptions) => {
  const { variation: newFormAmountInput } = useExperimentFlag(
    'new-form-amount-input',
    false
  );

  const {
    config: {
      amount: { max: maxLoanAmount, min: minLoanAmount, step: stepLoanAmount },
      duration: { max: maxLoanDuration, min: minLoanDuration },
    },
    rates: { min: minRate = 0 },
  } = ConfigService.get('product-unsecured-loan');

  const { t } = useTranslation();

  const [showMore, setShowMore] = useState(false);

  const { baseUrl, onChange, onSubmit, onPreSubmit, axoFormData } =
    useAxoFormContext<Step1Data>();

  const form = useForm({
    defaultValues: useAxoFormDefaultValues(options),
    resolver: zodResolver(finlandStep1Schema),
    mode: 'onBlur',
  });

  const {
    watch,
    setValue,
    setError,
    formState: { isSubmitting, isSubmitSuccessful, touchedFields, dirtyFields },
  } = form;

  useInitiatedTracking({
    shouldTriggerOnInitiated: Object.keys(touchedFields).length > 0,
  });

  const loading = isSubmitting || isSubmitSuccessful;

  const showMoreFields =
    showMore ||
    axoFormData['Customer/Email'] ||
    touchedFields['LoanApplication/InitialPurpose'] ||
    dirtyFields['LoanApplication/InitialPurpose'];

  const { showMarketingConsent = true } = useMarketingConsent({
    marketingConsent: axoFormData['Ignore/MarketingConsent'],
    shouldShowMarketingConsent: axoFormData['Ignore/ShowMarketingConsent'],
  });

  const { EmailPrompt, isValid, formError, enableEmailValidation } =
    useEmailValidation({
      baseUrl,
      email: watch('Customer/Email'),
      setEmail: (email) => setValue('Customer/Email', email),
    });

  const emailRegisterOptions = getEmailRegisterOptions(enableEmailValidation);

  const appliedAmount = axoFormData['LoanApplication/AppliedAmount'] ?? 0;
  const loanDuration = axoFormData['LoanApplication/LoanDuration'] ?? 0;
  const showRefinanceSaving =
    watch('LoanApplication/InitialPurpose') === Purpose.RefinancingLoan;

  const displayRecommended = verifyThreshold([
    { limit: 10_000, duration: 5 },
    { limit: 20_000, duration: 7 },
    { limit: 30_000, duration: 9 },
    {
      limit: maxLoanAmount,
      duration: 12,
    },
  ])({
    amount: appliedAmount,
    duration: loanDuration,
  });

  const monthlyPayment = calculateMonthlyPayment({
    amount: appliedAmount,
    duration: loanDuration,
    rate: minRate,
  });

  const keyBenefits = t('KeyBenefits', {
    returnObjects: true,
    defaultValue: [],
  }) as string[];

  const _onSubmit = async (data: Step1Data) => {
    if (!isValid) {
      setError('Customer/Email', { message: 'Email.error.pattern' });
      return;
    }

    if (isPre) {
      onPreSubmit(data);
      return;
    }

    onSubmit(data);
  };

  return (
    <FormProvider {...form}>
      <Form
        onSubmit={_onSubmit}
        onChange={onChange}
        /* We need to override the touched attribute for this for it to trigger a change immediately when it is interacted with.
         Otherwise, the user would need to interact with another field before the change is triggered.*/
        touchedFieldsOverride={touchedFieldsOverride}
      >
        <Stack gap={'m'}>
          {newFormAmountInput ? (
            <SliderField<Step1Data>
              name="LoanApplication/AppliedAmount"
              title={t('AppliedAmount.label')}
              data-testid="applied-amount-input"
              titleAsHeading
              min={minLoanAmount}
              max={maxLoanAmount}
              step={stepLoanAmount}
              curConfig={{
                locale: ConfigService.get('global').i18n.defaultLocale,
                currency: ConfigService.get('global').currency,
                fractionDigits: 0,
              }}
            />
          ) : (
            <>
              <span className={styles.appliedAmount}>
                <NumberFieldUI<Step1Data>
                  name="LoanApplication/AppliedAmount"
                  label={t('AppliedAmount.label')}
                  data-testid="applied-amount-input"
                  autoSetValues
                  size="large"
                  suffix={t('suffix.currency')}
                  format={formatSpacedNumber}
                  disableSuccessState={true}
                  registerOptions={{
                    max: maxLoanAmount,
                    min: minLoanAmount,
                    valueAsNumber: true,
                  }}
                />
              </span>

              <SliderFieldUI<Step1Data>
                name="LoanApplication/AppliedAmount"
                min={minLoanAmount}
                max={maxLoanAmount}
                step={stepLoanAmount}
                valueSuffix={t('suffix.currency')}
              />
            </>
          )}

          <LoanDurationField<Step1Data>
            name="LoanApplication/LoanDuration"
            country="fi"
            maxDuration={maxLoanDuration}
            minDuration={minLoanDuration}
            recommended={displayRecommended}
            loanAmountPerMonth={monthlyPayment}
          />

          {/* TODO move this to LoanDurationSelector component */}
          {!displayRecommended && (
            <Message hasIcon variant={'warn'} size={'s'}>
              {t('DurationWarning')}
            </Message>
          )}

          <Stack className={styles.initialPurpose} gap={'4xs'}>
            <RadioFieldUI<Step1Data>
              name="LoanApplication/InitialPurpose"
              label={t('InitialPurpose.label')}
              data-testid="initial-purpose-input"
              radioStyle="both"
              options={initialPurposeOptions}
              {...initialPurposeFieldOptions}
            />
            {showRefinanceSaving && (
              <span className={styles.refinanceSaving}>
                <RefinanceSaving />
              </span>
            )}
          </Stack>
        </Stack>

        {showMoreFields ? (
          <>
            <PhoneFieldUI<Step1Data>
              name="Customer/MobilePhoneNumber"
              label={t('MobilePhoneNumber.label')}
              data-testid="phone-number-input"
              {...phoneFieldOptions}
            />

            <EmailFieldUI<Step1Data>
              name="Customer/Email"
              label={t('Email.label')}
              data-testid="email-input"
              registerOptions={emailRegisterOptions}
              error={formError}
              confirmationPrompt={<EmailPrompt />}
            />

            <Consent showMarketingConsent={showMarketingConsent}>
              <Consent.TermsAndConditions />
              <Consent.Marketing />
            </Consent>

            <Navigation.Continue loading={loading} size={isPre ? 'xl' : 'l'}>
              {t('next')}
            </Navigation.Continue>
          </>
        ) : (
          <Button
            type="button"
            variant="primary"
            size="xl"
            data-testid="button-show-more"
            onClick={() => setShowMore(true)}
            suffix={<Icon name="arrow-right" />}
          >
            {t('next')}
          </Button>
        )}
      </Form>

      {isPre && (
        <USPsList gap="3xs">
          {keyBenefits?.map((benefit, index) => (
            <USPsList.Item key={index}>
              <Trans t={t}>{benefit}</Trans>
            </USPsList.Item>
          ))}
        </USPsList>
      )}
    </FormProvider>
  );
};
